import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, MapPin, ChevronRight } from "lucide-react";
import { teacherApi } from "@/lib/api";

/* Subtle "Ostatnia aktywność" strip beneath the dashboard header. Shows when
   the previous login happened + where (from GeoIP) + browser. Links to the
   full login history under /teacher/settings. Renders nothing on a first-
   ever login or if the endpoint fails. */
export default function LastActivityWidget() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    teacherApi.get("/auth/last-login")
      .then(({ data }) => { if (mounted) setData(data); })
      .catch(() => { /* widget is informational — silent fail */ });
    return () => { mounted = false; };
  }, []);

  if (!data || !data.has_previous) return null;

  const when = (() => {
    try { return relTime(data.ts); }
    catch { return data.ts; }
  })();
  const location = data.location || data.ip || "nieznana lokalizacja";

  return (
    <Link
      to="/teacher/settings"
      data-testid="last-activity-widget"
      className={`brut brut-hover border-2 border-[#0F172A] rounded-md
                 px-3 py-2 mb-4 flex items-center gap-3 text-sm
                 transition-colors ${
                   data.is_location_change
                     ? "bg-[#FEF08A] hover:bg-[#FDE68A] animate-[pulse-brut_3s_ease-in-out_3]"
                     : "bg-white hover:bg-[#F8FAFC]"
                 }`}
    >
      <Clock className={`w-4 h-4 shrink-0 ${
        data.is_location_change ? "text-[#0F172A]" : "text-[#64748B]"}`}
             strokeWidth={2.5} />
      <span className="text-[#334155] flex-1 min-w-0 truncate">
        <span className="font-bold text-[#0F172A]">
          {data.is_location_change
            ? "Uwaga — zmiana lokalizacji:"
            : "Ostatnia aktywność:"}
        </span>
        {" "}Zalogowano {when} z{" "}
        <span className="inline-flex items-center gap-1">
          <MapPin className={`w-3.5 h-3.5 inline ${
            data.is_location_change ? "text-[#B91C1C]" : ""}`}
                  strokeWidth={2.5} />
          <span className="font-mono">{location}</span>
        </span>
        {data.browser && data.browser !== "Other" && data.browser !== "Unknown"
          ? <> · {data.browser}</>
          : null}
      </span>
      <ChevronRight className="w-4 h-4 shrink-0 text-[#64748B]" strokeWidth={2.5} />
    </Link>
  );
}

/* Human-friendly relative time in Polish ("5 min temu", "3 godz. temu", "wczoraj"). */
function relTime(iso) {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));
  if (diffSec < 60) return "przed chwilą";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min temu`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} godz. temu`;
  const days = Math.floor(diffSec / 86400);
  if (days === 1) return "wczoraj";
  if (days < 7) return `${days} dni temu`;
  return new Date(iso).toLocaleDateString("pl");
}
